'use client';

import { TAURI_TITLEBAR_HEIGHT_PX } from '@/components/layout/header/tauri-drag-spacer';
import { isMac } from '@/hooks/platform';
import {
  tauriWindowController,
  ZOOM_FACTOR_CHANGED_EVENT,
} from '@/tauri/controller/window';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { managedListen, type ManagedUnlistenFn } from '@heidi/plugin-core';
import { useEffect, useLayoutEffect } from 'react';

// Publishes the desktop title-bar height as `--app-titlebar-height` on
// `<html>`. Consumers (design-system sheets/tooltips, page shells' top
// padding, the drag spacer's own height) offset below the bar via
// `var(--app-titlebar-height, 0px)` — the 0px fallback covers every window
// without the bar (web, auxiliary Tauri windows), so the gate is "this window
// has a title bar", not "is Tauri".
//
// The value is always a RESOLVED px string (`32px`, `64px`), never a calc():
// the design-system tooltip reads this inline style with `Number.parseFloat`
// to build its Radix collisionPadding, and `parseFloat('calc(…)')` is NaN.
//
// On macOS the height divides by `min(zoom factor, 1)` because the traffic
// lights are native chrome: `set_zoom` scales the page but not them, so at
// 0.5x a plain 32px bar renders 16 physical points tall while the native
// cluster stays ~28 points — the lights spill onto page content, and clicks
// there are consumed by the OS drag strip (the APP-4382 bug). Dividing by the
// factor keeps the bar (and everything padding below it) at a constant
// physical size when zoomed out. The `min(…, 1)` clamp leaves zooming IN
// untouched — a taller bar already clears the cluster, and the bar's contents
// (which scale with the page) would overflow a counter-held bar at 2x.
// Windows keeps a constant 32px: its window controls are React-rendered, so
// chrome and page scale together.
//
// Mounted eagerly in `_app.tsx`'s full provider tree — NOT inside the
// dynamically-imported `TauriAppHeader` — so page shells can use the titlebar
// CSS variable for padding before the header chunk loads. `useLayoutEffect`
// applies the var in the same frame as hydration.
export const TauriTitlebarHeightVar = () => {
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--app-titlebar-height',
      `${TAURI_TITLEBAR_HEIGHT_PX}px`
    );
    return () => {
      document.documentElement.style.removeProperty('--app-titlebar-height');
    };
  }, []);

  // macOS only: mirror the Rust-owned zoom factor into `--app-zoom-factor`
  // (consumed by `tauri-app-header.tsx`'s traffic-light inset calc) and fold
  // it into the published height. Seeded via `zoom_factor` (the restored
  // factor is applied before the page can attach a listener), then kept live
  // via `ZOOM_FACTOR_CHANGED_EVENT`, which fires for the keyboard shortcuts
  // and the native View menu alike.
  useEffect(() => {
    if (!isTauri() || !isMac) return undefined;

    let released = false;
    let unlisten: ManagedUnlistenFn | undefined;

    const publish = (factor: number) => {
      // `released` guards the async paths: a seed promise or event resolving
      // after unmount must not re-write vars the cleanup already removed.
      // The Rust side clamps to the 0.5–2.0 ladder; guard the value anyway so
      // a bad payload can never wedge the chrome at a nonsense size.
      if (released || !Number.isFinite(factor) || factor <= 0) return;
      document.documentElement.style.setProperty('--app-zoom-factor', String(factor));
      document.documentElement.style.setProperty(
        '--app-titlebar-height',
        `${TAURI_TITLEBAR_HEIGHT_PX / Math.min(factor, 1)}px`
      );
    };

    tauriWindowController
      .zoom_factor()
      .then(publish)
      .catch((error) => {
        logger.error('[desktop-zoom] Failed to read the zoom factor', error);
      });

    void managedListen<number>(ZOOM_FACTOR_CHANGED_EVENT, (event) => {
      publish(event.payload);
    })
      .then((release) => {
        if (released) {
          void release();
        } else {
          unlisten = release;
        }
      })
      .catch((error) => {
        logger.error('[desktop-zoom] Failed to subscribe to zoom changes', error);
      });

    return () => {
      released = true;
      void unlisten?.();
      document.documentElement.style.removeProperty('--app-zoom-factor');
    };
  }, []);

  return null;
};
